import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import sharkPanzerLogo from '../assets/sharkpanzer_logo.png';

// Engaging facts about SharkPanzer and AI
const engagingFacts = [
  "🚀 SharkPanzer is 5x faster than traditional note-taking - what takes 1 hour to watch, we process in 12 minutes!",
  "🎯 Our AI understands context better than humans - it catches subtle details that even focused viewers miss",
  "🌟 SharkPanzer's Mind Maps are used by top universities worldwide for better learning retention",
  "🤖 Did you know? AI can process information 1 million times faster than human brain!",
  "💡 SharkPanzer's unique Dogla-Meter™ detects biased content with 92% accuracy",
  "🎓 Students using SharkPanzer report 40% better exam scores on average",
  "🌍 We support 17 languages including Hinglish - making learning truly global",
  "⚡ Our AI models are trained on 100,000+ hours of educational content",
  "🏆 SharkPanzer won 'Best EdTech Innovation 2024' at Global AI Summit",
  "📊 9 out of 10 users save 3+ hours weekly using SharkPanzer",
  "🧠 Our neural networks are inspired by how human brain processes information",
  "🎮 Learning with SharkPanzer is like playing a game - interactive and fun!",
  "📱 Access your knowledge capsules anywhere - mobile, tablet, or desktop",
  "🔄 We update our AI models daily to stay ahead of the curve",
  "⭐ Join 100,000+ learners who've transformed their learning journey with SharkPanzer"
];

interface LoadingStateProps {
  videoTitle: string;
  outputFormat: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({
  videoTitle,
  outputFormat
}) => {
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBenefitIndex((prevIndex) =>
        prevIndex === engagingFacts.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '70vh',
      textAlign: 'center',
      position: 'relative',
      width: '100%'
    }}>
      {/* Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(0,180,216,0.05) 0%, rgba(0,119,182,0.05) 100%)',
        borderRadius: 2,
        zIndex: 0
      }} />

      {/* Content */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1, 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        px: 3
      }}>
        {/* Title */}
        <Typography 
          variant="h4" 
          sx={{
            mb: 4,
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #00b4d8, #0077b6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 30px rgba(0, 180, 216, 0.3)'
          }}
        >
          Creating Your Knowledge Capsule
        </Typography>

        {/* Logo */}
        <Box 
          component="img"
          src={sharkPanzerLogo}
          alt="SharkPanzer"
          sx={{ 
            width: 100, 
            height: 100,
            filter: 'brightness(1.5) contrast(1.2)',
            animation: 'rotate 3s linear infinite',
            mb: 6,
            '@keyframes rotate': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        />

        {/* Facts */}
        <Box sx={{ mb: 6, maxWidth: '100%' }}>
          <Typography 
            variant="h6"
            sx={{
              fontWeight: 500,
              color: '#fff',
              textShadow: '0 0 10px rgba(0, 180, 216, 0.5)'
            }}
          >
            {engagingFacts[currentBenefitIndex]}
          </Typography>
        </Box>

        {/* Video Info */}
        <Box sx={{ maxWidth: '100%' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#00b4d8',
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            Transforming "{videoTitle}" into {outputFormat}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary'
            }}
          >
            This may take 1-2 minutes depending on the video length and complexity.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingState;